import { Injectable, EventEmitter } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, of, Subscriber, NEVER, throwError } from 'rxjs';
import { map, tap, mergeMap, catchError } from 'rxjs/operators';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { HttpHeaders } from '@angular/common/http';

import {
  LoginApiModel,
  ActiveDirectoryLoginApiModel,
  TokensApiModel,
  RefreshApiModel,
  ChangeProgramApiModel,
  CheckResetTokenApiModel
} from '@app/api/api-models';
import { RecoverPasswordApiModel } from '@app/api/api-models/auth/RecoverPasswordApiModel';
import { ResetPasswordApiModel } from '@app/api/api-models/auth/ResetPasswordApiModel';
import { RouteHelperService } from '@app/shared/services/route-helper.service';
import { ActiveDirectoryConfigService } from '@app/shared/services/active-directory-config.service';
import { PageStateService } from '@app/shared/services/page-state.service';
import { TokenStorageService } from '@app/shared/services/token-storage.service';
import { HttpClientService } from '@app/api/api-services/http-client.service';
import { ConfigService } from '@app/configuration/config.service';
import { EidAuthService } from '@app/auth/services/eid-auth.service';
import { LogoutMode } from '@app/auth/enums/logout-mode.enum';
import { LOGOUT_REQUESTED_MESSAGE, authBroadcastChannel } from '@app/shared/auth-broadcast-channel';
import { NavNotificationsService } from '@api/api-services/nav-notifications.service';
import { LayoutService } from '@app/shared/services/layout.service';
import { NeoDialog } from '@neogov/pattern-library/neo-dialog';

@Injectable()
export class AuthService {
  public programChange = new EventEmitter<string>();
  public changeInitialPasswordModalRef: BsModalRef;
  private tokenRefreshEmitter: EventEmitter<TokensApiModel>;

  private apiUrl = 'auth';

  constructor(
    private readonly http: HttpClientService,
    private readonly tokenStorageService: TokenStorageService,
    private readonly routeHelper: RouteHelperService,
    private readonly activeDirectoryConfigService: ActiveDirectoryConfigService,
    private readonly pageStateService: PageStateService,
    private readonly eidAuthService: EidAuthService,
    private readonly layoutService: LayoutService,
    private readonly notificationsService: NavNotificationsService,
    private readonly dialog: NeoDialog,
    private readonly configService: ConfigService
  ) {
    this.changeInitialPasswordModalRef = null;
  }

  public isAuthenticated(): boolean {
    return !!this.tokenStorageService.userInfo && !!this.tokenStorageService.userInfo.UserId;
  }

  public get isPowerDmsLoginEnabled(): boolean {
    return this.configService.getConfig().authSettings.ssoEnabled;
  }

  public getToken(): Observable<string> {
    const token = this.tokenStorageService.getAccToken();

    if (!token || this.tokenStorageService.isTokenExpired(token)) {
      return this.requestRefreshAccToken();
    } else {
      return of(token);
    }
  }

  public login(loginModel: LoginApiModel): Observable<TokensApiModel> {
    return this.http
      .post<TokensApiModel>(`${this.apiUrl}/login`, loginModel)
      .pipe(tap((tokens) => this.tokenStorageService.tokensChange.emit(tokens)));
  }

  public activeDirectoryLogin(loginModel: ActiveDirectoryLoginApiModel): Observable<TokensApiModel> {
    return this.http.post<TokensApiModel>(`${this.apiUrl}/ad-login`, loginModel).pipe(
      map((tokens) => {
        this.tokenStorageService.tokensChange.emit(tokens);
        return tokens;
      })
    );
  }

  public loginEid(token: string): Observable<TokensApiModel> {
    const options = {
      headers: new HttpHeaders({
        Authorization: `Bearer ${token}`
      })
    };

    return this.http
      .post<TokensApiModel>(`${this.apiUrl}/login-eid`, null, options)
      .pipe(tap((tokens) => this.tokenStorageService.tokensChange.emit(tokens)));
  }

  public logout(mode: LogoutMode, shouldBroadCast = false): Observable<void> {
    this.layoutService.showProgramSwitcher = false;
    this.notificationsService.emitNotificationsStateObserver(false);

    if (mode != LogoutMode.Manual) {
      this.clientLogout(shouldBroadCast);
      return of(null);
    }

    const url = `${this.apiUrl}/logout`,
      refreshModel: RefreshApiModel = {
        RefreshToken: this.tokenStorageService.getRefToken(),
        ClientId: this.tokenStorageService.getClientId()
      };
    return this.http
      .post<void>(url, refreshModel)
      .pipe(
        tap(() => {
          this.clientLogout(shouldBroadCast);
        })
      )
      .pipe(
        catchError(() => {
          this.clientLogout(shouldBroadCast);
          return of(null);
        })
      );
  }

  public clientLogout(shouldBroadCast: boolean): void {
    // Close all open dialogs and flyouts before logout
    this.dialog.closeAll();

    if (shouldBroadCast) {
      authBroadcastChannel.postMessage(LOGOUT_REQUESTED_MESSAGE);
    }
    if (
      this.tokenStorageService.userInfo.CurrentSite.PowerSuitEidAuthEnabled &&
      !this.tokenStorageService.userInfo.IsSysAdmin
    ) {
      this.eidAuthService.logout();
      this.tokenStorageService.clearStorageData();
    } else {
      this.activeDirectoryConfigService.logout();
      this.tokenStorageService.clearStorageData();
      this.routeHelper.login(true);
    }
  }

  private updateRefToken(token: string): Observable<TokensApiModel> {
    if (this.tokenRefreshEmitter) {
      return this.tokenRefreshEmitter;
    }

    this.tokenRefreshEmitter = new EventEmitter<TokensApiModel>();

    const url = `${this.apiUrl}/refresh`,
      refreshModel: RefreshApiModel = {
        RefreshToken: token,
        ClientId: this.tokenStorageService.getClientId()
      };

    this.activeDirectoryConfigService
      .init()
      .pipe(
        mergeMap((isInitialized) =>
          isInitialized
            ? this.activeDirectoryConfigService.accessToken.pipe(
                tap((adToken) => (refreshModel.ActiveDirectoryToken = adToken))
              )
            : of(null)
        )
      )
      .pipe(
        mergeMap(() => (this.pageStateService.isRefreshing ? NEVER : this.http.post<TokensApiModel>(url, refreshModel)))
      )
      .subscribe(
        (tokens) => {
          this.tokenStorageService.tokensChange.emit(tokens);
          this.tokenRefreshEmitter.emit(tokens);
          this.tokenRefreshEmitter.observers.forEach((s) => (s as Subscriber<TokensApiModel>).unsubscribe());
          this.tokenRefreshEmitter = null;
        },
        (error: HttpErrorResponse) => {
          if (error?.status >= 400 && error.status < 500) {
            this.tokenStorageService.clearStorageData();
            this.routeHelper.login(true);
          }

          this.tokenRefreshEmitter.error(error);
          this.tokenRefreshEmitter = null;
        }
      );

    return this.tokenRefreshEmitter;
  }

  public changeCurrent(
    siteId: number,
    programId: number | null = null,
    emitProgramChangeEvent = true
  ): Observable<TokensApiModel> {
    const url = `${this.apiUrl}/change-current`,
      refreshModel: ChangeProgramApiModel = {
        RefreshToken: this.tokenStorageService.getRefToken(),
        ClientId: this.tokenStorageService.getClientId(),
        SiteId: siteId,
        ProgramId: programId
      };

    return this.activeDirectoryConfigService
      .init()
      .pipe(
        mergeMap((isInitialized) =>
          isInitialized
            ? this.activeDirectoryConfigService.accessToken.pipe(
                tap((adToken) => (refreshModel.ActiveDirectoryToken = adToken))
              )
            : of(null)
        )
      )
      .pipe(mergeMap(() => this.http.post<TokensApiModel>(url, refreshModel)))
      .pipe(
        map((tokens: TokensApiModel) => {
          this.tokenStorageService.tokensChange.emit(tokens);

          if (emitProgramChangeEvent) {
            this.programChange.emit(tokens.AccessToken);
          }

          return tokens;
        })
      );
  }

  public recoverPassword(model: RecoverPasswordApiModel): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/recover-password`, model);
  }

  public checkResetToken(model: CheckResetTokenApiModel): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/check-reset-token`, model);
  }

  public resetPassword(model: ResetPasswordApiModel): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/reset-password`, model);
  }

  public requestRefreshAccToken(): Observable<string> {
    const token = this.tokenStorageService.getRefToken();

    if (!token || this.tokenStorageService.isTokenExpired(token)) {
      this.clientLogout(false);
      return throwError(null);
    }

    return this.updateRefToken(token).pipe(map((x) => x.AccessToken));
  }

  public eidAdminChangePasswordRedirect(targetGlobalUserGuid: string, accessToken: string): Observable<string> {
    const url = this.configService.getConfig().EIDAdminChangePasswordUrl + `/accounts/${targetGlobalUserGuid}/reset-credentials/link`;

    //window.location.origin
    const body = {
      returnurl: window.location.origin + '/eid-appswitch'
    };

    const httpOptions = {
      headers: new HttpHeaders({
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      })
    };

    return this.http.post(url, body, httpOptions, true).pipe(
      map((response) => {
        const adminPasswordResetLink = response['adminPasswordResetLink'];
        return adminPasswordResetLink;
      }),
      catchError((error) => {
        return throwError(error);
      })
    );
  }
}
